import type { ActivationLifecycle } from '@/infra/activation'
import type { GetCurrentWorkspaceFn } from '@/features/iam'
import { action, computed, observable, runInAction } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { Checklist } from '@/features/jobs/details/tax-type/info-gathering/Checklist'
import type { MeasureOneStore } from '@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneStore'
import { Id } from '@/infra/id'
import {
  MEASURE_ONE_SUPPORTED_DOCUMENT_TYPES,
  type MeasureOneExperience,
  type WellKnownDocumentTypeShortId,
} from '@/features/jobs/details/tax-type/info-gathering/measure-one/mappers'
import { task } from 'mobx-task'
import { DocumentSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/documents/DocumentSupportingItemInput'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import env from '@/infra/env'
import { type JobTaxDataImport } from '@/features/jobs/details/tax-type/info-gathering/measure-one/JobTaxDataImport'
import type { AvailableItem } from '@/features/jobs/details/tax-type/info-gathering/measure-one/MeasureOneWizardView/SelectDocumentsView'
import { onRpcNotFoundReturnNull } from '@/utils/grpc-util'
import { links } from '@/utils/link'

export class MeasureOneViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Modal for starting the measure one import documents flow
   */
  readonly measureOneImportModalVm = new ModalViewModel()

  /**
   * Flag to know when to keep showing the import modal.
   */
  @observable accessor importModalDismissed = false

  /**
   * fetched access token for measure one.
   */
  @observable accessor accessToken: string | null = null

  /**
   * fetched data request id for measure one.
   */
  @observable accessor dataRequestId: string | null = null

  /**
   * Measureone requested data source id.
   */
  @observable accessor dataSourceId: string | null = null

  /**
   * Measureone requested data source name.
   */
  @observable accessor dataSourceName: string | null = null

  /**
   * Current data import data for the job.
   */
  @observable accessor jobTaxDataImport: JobTaxDataImport | null = null

  /**
   * Selected items to import from measureone
   */
  @observable private accessor _selectedItems = new Map<Id, AvailableItem>()

  constructor(
    private submittedJob: SubmittedJob,
    private getCheckList: () => Checklist | null,
    private readonly _getCurrentWorkspace: GetCurrentWorkspaceFn,
    private readonly measureOneStore: MeasureOneStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
  ) {}

  /**
   * Whether the bottom bar is visible or not
   */
  @computed
  get canShowBottomBar() {
    return this.dataSourceId && this.dataSourceName
  }

  /**
   * Weather m1 is enabled for the given workspace
   */
  @computed
  get isEnabled() {
    return (
      this._getCurrentWorkspace().config.measureOne.enabled &&
      this.measureOneSupportedDataTypes.length > 0
    )
  }

  /**
   * All requested documents
   */
  @computed
  get requestedDocumentItems() {
    return this.getCheckList()?.allItems ?? []
  }

  /**
   * M1 documents that are compatible with our requested documents
   */
  @computed
  get measureOneSupportedDataTypes() {
    // E2E doc types short ID must have a suffix on E2E tests
    // this will map the id generated for the e2e and map it to a valid doc type
    if (this.submittedJob.workspace.name.includes('E2E-')) {
      const wsName = this.submittedJob.workspace.name

      return this.requestedDocumentItems.map(({ input }) => {
        const value = input as DocumentSupportingItemInput
        return Id.assert(value.documentTypeShortId).replace(
          `_${wsName}`,
          '',
        ) as WellKnownDocumentTypeShortId
      })
    }

    return this.requestedDocumentItems
      .map((x) => x.input)
      .filter(
        (input): input is DocumentSupportingItemInput =>
          input instanceof DocumentSupportingItemInput &&
          MEASURE_ONE_SUPPORTED_DOCUMENT_TYPES.has(
            Id.assert(input.documentTypeShortId),
          ),
      )
      .map((input) => input.documentTypeShortId as WellKnownDocumentTypeShortId)
  }

  /**
   * Weather we can show the import modal when first reaching info gathering
   */
  @computed
  get canShowImportModal() {
    return (
      this.isEnabled &&
      !this.importModalDismissed &&
      this.measureOneSupportedDataTypes.length > 0 &&
      Boolean(!this.jobTaxDataImport?.id)
    )
  }

  /**
   * MeasureOne widget required config
   */
  @computed
  get measureOneConfig() {
    return {
      access_key: this.accessToken,
      host_name: env.MEASUREONE_HOST_NAME,
      datarequest_id: this.dataRequestId,
    }
  }

  /**
   * Job tax data import items
   */
  @computed
  get items() {
    return this.jobTaxDataImport?.items
  }

  /**
   * How many items are selected
   */
  @computed
  get selectedItemsCount() {
    return Boolean(this._selectedItems.size)
  }

  /**
   * Whether the import can start
   */
  @computed
  get canImportDocuments() {
    return Boolean(this._selectedItems.size)
  }

  /**
   * All selected items
   */
  @computed
  get selectedItemIds() {
    return new Set(this._selectedItems.keys())
  }

  /**
   * Whether we can navigate to the import screen
   */
  @computed
  get canNavigateToImportScreen() {
    return (
      this.jobTaxDataImport &&
      (this.jobTaxDataImport.status === 'DataRequestCompleted' ||
        this.jobTaxDataImport.status === 'DocumentsUploadCompleted')
    )
  }

  /**
   * Whether items are being loaded
   */
  @computed
  get isLoadingItems() {
    return this.jobTaxDataImport?.status === 'DataRequestInProgress'
  }

  /**
   * Which link we should navigate to
   */
  @computed
  get autoImportDocumentsLink() {
    return this.canNavigateToImportScreen
      ? links.measureOneSelectDocuments(this.submittedJob.id)
      : links.measureOneSelectExperienceType(this.submittedJob.id)
  }

  activate() {
    this._lifecycle.activate()

    void this.maybeShowImportModal()
  }

  /**
   * Whether we should show the import modal on job loading
   */
  async maybeShowImportModal() {
    if (this.canShowImportModal) {
      await this.getJobTaxDataImport()

      this.measureOneImportModalVm.show()
    }
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Dismisses the connection modal and makes sure we don't show it again
   */
  @action.bound
  dismissModal() {
    this.measureOneImportModalVm.close()

    this.importModalDismissed = true
  }

  /**
   * Handle item selection
   */
  @action.bound
  toggleItemSelection(item: AvailableItem) {
    if (this._selectedItems.has(item.id)) {
      this._selectedItems.delete(item.id)
    } else {
      this._selectedItems.set(item.id, item)
    }
  }

  /**
   * Get access token from api
   */
  generateAccessToken = task.resolved(async () => {
    const accessToken = await this.measureOneStore.generatePublicAccessToken(
      Id.assert(this.submittedJob.id),
    )

    runInAction(() => {
      this.accessToken = accessToken
    })
  })

  /**
   * Fetches the data we need to initialize the measure one import flow
   */
  initializeMeasureOneImportFlow = task.resolved(
    async (experience: MeasureOneExperience) => {
      try {
        const dataRequestId =
          await this.measureOneStore.initializeJobTaxDataImport(
            Id.assert(this.submittedJob.id),
            Id.assert(this.submittedJob.workspace.id),
            experience,
            this.measureOneSupportedDataTypes,
          )

        await this.generateAccessToken()

        await this.getJobTaxDataImport()

        runInAction(() => {
          this.dataRequestId = dataRequestId
        })
      } catch (e) {
        this._createSnackbarErrorHandler('Unable to initialize measure one')(e)
      }
    },
  )

  /**
   * Start the measure one import flow
   */
  connectJobTaxDataImport = task.resolved(
    async (dataSourceId: string, dataSourceName: string) => {
      if (!this.dataRequestId) {
        console.error('Unable to connect measure one: data request id is null')
        return
      }

      await this.measureOneStore
        .connectJobTaxDataImport(
          this.dataRequestId,
          dataSourceId,
          dataSourceName,
          Id.assert(this.submittedJob.id),
        )
        .catch(
          this._createSnackbarErrorHandler('Unable to connect measure one'),
        )

      runInAction(() => {
        this.dataSourceId = dataSourceId
        this.dataSourceName = dataSourceName
      })
    },
  )

  /**
   * Get current job tax data import
   */
  getJobTaxDataImport = task.resolved(async () => {
    const jobTaxDataImport = await this.measureOneStore
      .getJobTaxDataImport(Id.assert(this.submittedJob.id))
      .catch(onRpcNotFoundReturnNull)

    if (!jobTaxDataImport) {
      return null
    }

    runInAction(() => {
      this.jobTaxDataImport = jobTaxDataImport
    })
  })

  importSelectedDocuments = task.resolved(async () => {
    if (this.selectedItemIds.size === 0) {
      console.error('Unable to import documents: no items selected')
      return
    }

    await this.measureOneStore
      .startDocumentsUpload(
        Id.assert(this.submittedJob.id),
        Array.from(this._selectedItems.values()),
      )
      .catch(this._createSnackbarErrorHandler('Unable to connect measure one'))

    this._selectedItems.clear()
  })
}
